import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TrendingDown, TrendingUp, Minus, Plus, ExternalLink, Search, Wallet, X } from 'lucide-react'
import { Layout } from '@/components/Layout'
import { supabase } from '@/services/SupabaseClient/supabase-client'
import { targetAssetsService } from '@/services'
import type { StockTrend } from '@/types/StockAnalysis'
import './stock-radar.css'

const TREND_CONFIG: Record<StockTrend, { label: string; icon: typeof TrendingUp; className: string }> = {
  growing:  { label: 'Crescendo', icon: TrendingUp,  className: 'stock-radar__badge--growing' },
  stable:   { label: 'Estável',   icon: Minus,        className: 'stock-radar__badge--stable' },
  declining:{ label: 'Em queda',  icon: TrendingDown, className: 'stock-radar__badge--declining' },
}

function formatQuarter(isoDate: string): string {
  const [year, month] = isoDate.split('-')
  const quarter = Math.ceil(Number(month) / 3)
  return `T${quarter}/${year.slice(2)}`
}

interface StockRadarCard {
  cnpj: string
  ticker: string
  name: string | null
  sector: string | null
  competence_month: string
  health_score: number
  trend: StockTrend
  positive_points: string[]
  negative_points: string[]
}

async function fetchStockAnalyses(): Promise<StockRadarCard[]> {
  const [{ data: analyses }, { data: assets }] = await Promise.all([
    supabase
      .from('latest_asset_ai_analyses')
      .select('cnpj, ticker, health_score, trend, positive_points, negative_points, competence_month')
      .eq('asset_type', 'ACAO')
      .order('competence_month', { ascending: false }),
    supabase.from('assets').select('ticker, long_name, short_name, sector').eq('type', 'ACAO'),
  ])

  if (!analyses?.length) return []

  const assetByTicker = new Map((assets ?? []).map((a) => [a.ticker, a]))

  return analyses
    .map((a) => {
      const asset = assetByTicker.get(a.ticker)
      return {
        cnpj: a.cnpj,
        ticker: a.ticker,
        name: asset?.short_name || asset?.long_name || null,
        sector: asset?.sector ?? null,
        competence_month: a.competence_month,
        health_score: a.health_score,
        trend: a.trend as StockTrend,
        positive_points: a.positive_points ?? [],
        negative_points: a.negative_points ?? [],
      }
    })
}

export function StockRadar() {
  const { data, isLoading } = useQuery({
    queryKey: ['stock-radar'],
    queryFn: fetchStockAnalyses,
    staleTime: 1000 * 60 * 5,
  })

  const stocks = data ?? []

  const { data: portfolioAssets } = useQuery({
    queryKey: ['target-assets'],
    queryFn: targetAssetsService.list,
    staleTime: 1000 * 60 * 5,
  })

  const portfolioTickers = useMemo(() => {
    const set = new Set<string>()
    for (const asset of portfolioAssets ?? []) {
      if (asset.quantity > 0) set.add(asset.ticker)
    }
    return set
  }, [portfolioAssets])

  const [search, setSearch] = useState('')
  const [sectorFilter, setSectorFilter] = useState('')
  const [trendFilter, setTrendFilter] = useState<StockTrend | ''>('')
  const [onlyPortfolio, setOnlyPortfolio] = useState(false)

  const sectors = useMemo(() => {
    const set = new Set<string>()
    for (const stock of stocks) {
      if (stock.sector) set.add(stock.sector)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [stocks])

  const hasActiveFilters = search.trim() !== '' || sectorFilter !== '' || trendFilter !== '' || onlyPortfolio

  const filteredStocks = useMemo(() => {
    const term = search.trim().toLowerCase()
    return stocks.filter((stock) => {
      if (term) {
        const matchesTerm =
          stock.ticker.toLowerCase().includes(term) || (stock.name?.toLowerCase().includes(term) ?? false)
        if (!matchesTerm) return false
      }
      if (sectorFilter && stock.sector !== sectorFilter) return false
      if (trendFilter && stock.trend !== trendFilter) return false
      if (onlyPortfolio && !portfolioTickers.has(stock.ticker)) return false
      return true
    })
  }, [stocks, search, sectorFilter, trendFilter, onlyPortfolio, portfolioTickers])

  const clearFilters = () => {
    setSearch('')
    setSectorFilter('')
    setTrendFilter('')
    setOnlyPortfolio(false)
  }

  return (
    <Layout>
      <section className="stock-radar app-page">
        <header className="stock-radar__header">
          <div>
            <h1>Ações Radar</h1>
            <p>Análises geradas por IA com dados trimestrais da CVM</p>
          </div>
        </header>

        {!isLoading && stocks.length > 0 && (
          <div className="stock-radar__filters">
            <div className="stock-radar__search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar por ticker ou nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)}>
              <option value="">Todos os setores</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>

            <select value={trendFilter} onChange={(e) => setTrendFilter(e.target.value as StockTrend | '')}>
              <option value="">Todas as tendências</option>
              <option value="growing">Crescendo</option>
              <option value="stable">Estável</option>
              <option value="declining">Em queda</option>
            </select>

            <button
              type="button"
              className="stock-radar__portfolio-toggle"
              data-active={onlyPortfolio}
              onClick={() => setOnlyPortfolio((prev) => !prev)}
            >
              <Wallet size={14} />
              Só minha carteira
            </button>

            {hasActiveFilters && (
              <button type="button" className="stock-radar__clear-filters" onClick={clearFilters}>
                <X size={14} />
                Limpar
              </button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="app-state app-state--loading">
            <p>Carregando análises...</p>
          </div>
        )}

        {!isLoading && stocks.length === 0 && (
          <div className="app-state app-state--empty">
            <p>Nenhuma análise disponível ainda.</p>
          </div>
        )}

        {!isLoading && stocks.length > 0 && filteredStocks.length === 0 && (
          <div className="app-state app-state--empty">
            <p>Nenhum resultado para os filtros aplicados.</p>
          </div>
        )}

        {!isLoading && filteredStocks.length > 0 && (
          <div className="stock-radar__grid">
            {filteredStocks.map((stock) => {
              const trendConfig = TREND_CONFIG[stock.trend]
              const TrendIcon = trendConfig?.icon

              return (
                <Link
                  key={stock.cnpj}
                  to={`/market/${stock.ticker}`}
                  className="stock-radar__card"
                >
                  <div className="stock-radar__card-top">
                    <div className="stock-radar__card-heading">
                      <span className="stock-radar__ticker">{stock.ticker}</span>
                      {stock.name && <span className="stock-radar__name">{stock.name}</span>}
                      <span className="stock-radar__quarter">{formatQuarter(stock.competence_month)}</span>
                    </div>
                    <span className="stock-radar__link-icon">
                      <ExternalLink size={14} />
                    </span>
                  </div>

                  <div className="stock-radar__score-row">
                    <span
                      className="stock-radar__score"
                      data-score={
                        stock.health_score >= 7 ? 'high' : stock.health_score >= 4 ? 'mid' : 'low'
                      }
                    >
                      <strong>{stock.health_score}</strong>
                      <small>/10</small>
                    </span>
                    {trendConfig && TrendIcon && (
                      <span className={`stock-radar__badge ${trendConfig.className}`}>
                        <TrendIcon size={12} />
                        {trendConfig.label}
                      </span>
                    )}
                  </div>

                  {(stock.positive_points[0] || stock.negative_points[0]) && (
                    <div className="stock-radar__highlights">
                      {stock.positive_points[0] && (
                        <p className="stock-radar__highlight stock-radar__highlight--positive">
                          <Plus size={12} />
                          <span>{stock.positive_points[0]}</span>
                        </p>
                      )}
                      {stock.negative_points[0] && (
                        <p className="stock-radar__highlight stock-radar__highlight--negative">
                          <Minus size={12} />
                          <span>{stock.negative_points[0]}</span>
                        </p>
                      )}
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </Layout>
  )
}
